import { t } from '@/i18n/i18n'
import { type GuestType } from '@/pages/guestTypes/GuestType.model'
import { GuestTypeService } from '@/pages/guestTypes/guestType.service'
import { PAGE_MODE, type PageMode } from '@/shared/constants'
import { ErrorService } from '@/shared/service/errorService'
import { SnackbarService } from '@/shared/snackbar/snackbar.service'
import { type GuestTypeId } from '@/shared/types/applicationData.type'
import { create } from '@/shared/utils/zustandVue'
import { cloneDeep } from '@/utils'

class GuestTypeState {
  mode: PageMode = PAGE_MODE.VIEW
  guestTypes: Record<string, GuestType> = {}
  currentGuestType: GuestType | null = null
  loading = false
  guestNames: string[] = []
  guestCounts: Record<GuestTypeId, number> = {} as Record<GuestTypeId, number>
}

type GuestTypeStore = GuestTypeState & {
  load: () => void
  setDeleting: (payload: GuestType) => void
  setView: () => void
  setGuestTypes: (guestTypes: GuestType[]) => void
  setAdding: () => void
  setEditing: (payload: GuestType) => void
  setGuestNames: (names: string[]) => void
  setGuestCounts: (counts: Record<GuestTypeId, number>) => void
  list: () => Promise<void>
  deleteGuestType: () => Promise<void>
  loadGuestNames: (guestType: GuestType) => Promise<void>
  addGuestType: (payload: GuestType) => Promise<void>
  editGuestType: (payload: GuestType) => Promise<void>
  reset: () => void
}

export const useGuestTypeStore = create<GuestTypeStore>((set, get) => ({
  ...new GuestTypeState(),
  load: () => {
    set({ loading: true })
  },
  setDeleting: (payload) => {
    set({ mode: PAGE_MODE.DELETE, currentGuestType: { ...payload } })
  },
  setView: () => {
    set({
      mode: PAGE_MODE.VIEW,
      currentGuestType: null,
      guestNames: [],
      loading: false,
    })
  },
  setGuestTypes: (guestTypes) => {
    set({
      guestTypes: guestTypes.reduce(
        (acc: Record<string, GuestType>, current) => {
          acc[current._id] = current
          return acc
        },
        {},
      ),
      loading: false,
    })
  },
  setAdding: () => {
    set({ mode: PAGE_MODE.ADD })
  },
  setEditing: (payload) => {
    set({ mode: PAGE_MODE.EDIT, currentGuestType: cloneDeep(payload) })
  },
  setGuestNames: (names) => {
    set({ guestNames: names, loading: false })
  },
  setGuestCounts: (counts) => {
    set({ guestCounts: counts })
  },
  async list() {
    try {
      get().load()
      get().setGuestTypes(await GuestTypeService.getAll())
      try {
        get().setGuestCounts(await GuestTypeService.getGuestCounts())
      } catch (e) {
        console.error('Failed to load guest counts', e)
      }
    } catch (e) {
      ErrorService.handleError(e)
    }
  },
  async deleteGuestType() {
    try {
      get().load()
      const { currentGuestType } = get()
      if (currentGuestType == null) return
      await GuestTypeService.deleteGuestType(currentGuestType._id)
      SnackbarService.info(t('GUEST_TYPE.SNACK_BAR.DELETE_SUCCESS'))
      await get().list()
    } catch (e) {
      ErrorService.handleError(e)
    } finally {
      get().setView()
    }
  },
  async loadGuestNames(guestType) {
    try {
      get().load()
      get().setGuestNames(
        await GuestTypeService.getGuestNamesByType(guestType._id),
      )
      get().setDeleting(guestType)
    } catch (e) {
      ErrorService.handleError(e)
      get().setView()
    }
  },
  async addGuestType(payload) {
    get().load()
    try {
      await GuestTypeService.addGuestType(payload)
      SnackbarService.info(
        t('GUEST_TYPE.SNACK_BAR.ADD_SUCCESS', {
          name: payload.names.fr,
        }),
      )
    } catch (e) {
      ErrorService.handleError(e)
    } finally {
      await get().list()
      get().setView()
    }
  },
  async editGuestType(payload) {
    get().load()
    try {
      await GuestTypeService.updateGuestType(payload)
      SnackbarService.info(
        t('GUEST_TYPE.SNACK_BAR.EDIT_SUCCESS', {
          name: payload.names.fr,
        }),
      )
    } catch (e) {
      ErrorService.handleError(e)
    } finally {
      await get().list()
      get().setView()
    }
  },
  reset: () => {
    set({ ...new GuestTypeState() })
  },
}))
